import React, { useState, useEffect, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, onSnapshot, doc, setDoc, query, deleteDoc, updateDoc } from 'firebase/firestore';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { RefreshCcw, TreeDeciduous, Leaf, Utensils, Plane, Zap, Award, Gauge, Trash2, TrendingUp, TrendingDown, Minus, Globe, X, CheckCircle } from 'lucide-react';

// --- Global Firebase Configuration (Mocked for runtime) ---
const firebaseConfig = {
    apiKey: "AIzaSyAj_nEaYG8mTZgA9C601ICuSrK37I-BxKY",
    authDomain: "ecobloom-9566c.firebaseapp.com",
    projectId: "ecobloom-9566c",
    storageBucket: "ecobloom-9566c.firebasestorage.app",
    messagingSenderId: "841256775674",
    appId: "1:841256775674:web:23460b1ec7508854aefe43",
    measurementId: "G-F7MPCQX6BW"
  };
const initialAuthToken = null; 
const appId = firebaseConfig.projectId;

const getUserDataCollectionPath = (userId) => {
  return `artifacts/${appId}/users/${userId}/carbonLogs`;
};

const getLeaderboardCollectionPath = () => {
    return `artifacts/${appId}/public/data/leaderboard`;
};
// ------------------------------------------------------------------------

// --- Data Source (Simulated External Emission Factors API) ---
const EMISSION_FACTORS_SOURCE = {
    'High_Meat_Meal': 3.5,
    'Medium_Meat_Meal': 1.8,
    'Plant_Based_Meal': 0.5,
    'Small_Car_100km': 15.0,
    'Public_Transport_100km': 5.0,
    'Flight_100km': 20.0,
    'Fast_Fashion_100USD': 30.0,
    'Second_Hand_100USD': 2.0,
};

const simulateApiCall = async (type, retries = 0) => {
    await new Promise(resolve => setTimeout(resolve, Math.random() * 300 + 200));
    if (Math.random() < 0.05 && retries < 3) {
        const delay = Math.pow(2, retries) * 1000;
        console.warn(`Simulated API call failed for ${type}. Retrying in ${delay / 1000}s...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return simulateApiCall(type, retries + 1);
    }
    const factor = EMISSION_FACTORS_SOURCE[type] || 0;
    if (retries > 0) {
        console.log(`API call successful after ${retries} retries.`);
    }
    return factor;
};

const calculateCarbonAsync = async (type, quantity) => {
    const factor = await simulateApiCall(type);
    return parseFloat((factor * quantity).toFixed(2));
};
// ------------------------------------------------------------------------

const formatChartDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatLogTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

// Static data for Bloom Actions
const bloomActions = [
    { title: "Join a Community Garden", icon: TreeDeciduous, category: "Diet", description: "Source local food and reduce transportation emissions." },
    { title: "Use E-Bike Share Program", icon: Leaf, category: "Travel", description: "Replace short car trips with zero-emission transit." },
    { title: "Plant 5 Native Trees", icon: TreeDeciduous, category: "General", description: "Offset previous emissions with verified local planting." },
    { title: "Try One Week Vegetarian", icon: Utensils, category: "Diet", description: "Significantly lower your weekly dietary footprint." },
    { title: "Choose Train over Flight (Short-Haul)", icon: Plane, category: "Travel", description: "Opt for lower-emission transport modes where feasible." },
];

// --- Onboarding Modal Component ---
const OnboardingModal = ({ onClose }) => (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-8 relative transform transition-all duration-300 scale-100">
            <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
                <X className="w-6 h-6" />
            </button>
            <h2 className="text-3xl font-extrabold text-green-700 mb-4 flex items-center">
                <Leaf className="w-8 h-8 mr-2" /> Welcome to EcoBloom!
            </h2>
            <p className="text-lg text-gray-700 mb-6">
                Your two-month journey from winter emissions to spring renewal starts here.
            </p>

            <div className="space-y-4">
                <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 mt-1 text-indigo-500 flex-shrink-0" />
                    <div className="ml-3">
                        <h4 className="font-bold text-gray-800">1. Log Your Activity</h4>
                        <p className="text-sm text-gray-600">Use the **Energy Input** form to log your travel, diet, and purchases. Our system uses real-world emission factors (via a serverless API simulation) to calculate the CO2e cost.</p>
                    </div>
                </div>
                <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 mt-1 text-indigo-500 flex-shrink-0" />
                    <div className="ml-3">
                        <h4 className="font-bold text-gray-800">2. Monitor Your Growth</h4>
                        <p className="text-sm text-gray-600">Watch your **Emissions Growth Curve** and track your **Bloom Level**. Keep your weekly emissions low to reach **Full Bloom (Level 4)**.</p>
                    </div>
                </div>
                <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 mt-1 text-indigo-500 flex-shrink-0" />
                    <div className="ml-3">
                        <h4 className="font-bold text-gray-800">3. Collaborate Globally</h4>
                        <p className="text-sm text-gray-600">See how your weekly footprint compares to others on the **Global Bloom Leaderboard**. Every new entry updates your score instantly!</p>
                    </div>
                </div>
            </div>

            <button
                onClick={onClose}
                className="w-full mt-8 py-3 px-4 rounded-md text-white font-bold bg-green-500 hover:bg-green-600 transition shadow-lg"
            >
                Start My EcoBloom Journey!
            </button>
        </div>
    </div>
);
// ------------------------------------


const App = () => {
    // --- Firebase/Auth State ---
    const [db, setDb] = useState(null);
    const [auth, setAuth] = useState(null);
    const [userId, setUserId] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);

    // --- Application State ---
    const [carbonLogs, setCarbonLogs] = useState([]);
    const [leaderboardData, setLeaderboardData] = useState([]); 
    const [currentEmissions, setCurrentEmissions] = useState({
        Travel: 0,
        Diet: 0,
        Purchasing: 0,
        Total: 0,
        Trend: 0, 
    });
    const [bloomLevel, setBloomLevel] = useState(1);
    // Updated Emojis for cleaner look
    const [bloomStatus, setBloomStatus] = useState({ emoji: '🌱', text: 'Dormant Seed' }); 
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [liveCarbonCost, setLiveCarbonCost] = useState(0.00); 
    const [showOnboarding, setShowOnboarding] = useState(true); 

    const [inputData, setInputData] = useState({
        category: 'Travel',
        type: 'Small_Car_100km',
        quantity: 1,
    });
    const [statusMessage, setStatusMessage] = useState('');

    // --- Live Carbon Cost Calculation Effect ---
    useEffect(() => {
        let isCancelled = false;
        const updateLiveCost = async () => {
            try {
                const cost = await calculateCarbonAsync(inputData.type, inputData.quantity);
                if (!isCancelled) {
                    setLiveCarbonCost(cost);
                }
            } catch (error) {
                console.error("Live calculation failed:", error);
                if (!isCancelled) {
                    setLiveCarbonCost(0.00); 
                }
            }
        };

        updateLiveCost();

        return () => {
            isCancelled = true;
        };
    }, [inputData.type, inputData.quantity]); 


    // --- 1. Firebase Initialization & Authentication Effect ---
    useEffect(() => {
        if (Object.keys(firebaseConfig).length === 0) {
            console.error("Firebase configuration is missing.");
            setIsLoading(false);
            return;
        }

        try {
            const app = initializeApp(firebaseConfig);
            const firestore = getFirestore(app);
            const authentication = getAuth(app);

            setDb(firestore);
            setAuth(authentication);

            const unsubscribe = onAuthStateChanged(authentication, (user) => {
                if (user) {
                    setUserId(user.uid);
                }
                setIsAuthReady(true);
            });

            const authenticate = async () => {
                try {
                    if (initialAuthToken) {
                        await signInWithCustomToken(authentication, initialAuthToken);
                    } else {
                        await signInAnonymously(authentication);
                    }
                } catch (error) {
                    console.error("Firebase Auth failed:", error);
                    await signInAnonymously(authentication); 
                }
            };
            
            authenticate();
            return () => unsubscribe();

        } catch (error) {
            console.error("Error initializing Firebase:", error);
            setIsLoading(false);
        }
    }, []);

    // --- 2. Real-Time Data Fetching (Private Logs) Effect ---
    useEffect(() => {
        if (!isAuthReady || !userId || !db) {
            setIsLoading(true);
            return;
        }

        const logsRef = collection(db, getUserDataCollectionPath(userId));
        const q = query(logsRef); 

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const logs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                co2e: parseFloat(doc.data().co2e),
            }));

            logs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            setCarbonLogs(logs);
            
            if (logs.length > 0) {
                setShowOnboarding(false);
            } else {
                setShowOnboarding(true);
            }
            
            const lastWeekLogs = logs.slice(-7);
            const previousWeekLogs = logs.slice(-14, -7);

            const sumEmissions = (logArray) => logArray.reduce((sum, log) => sum + log.co2e, 0);

            const lastWeekTotal = sumEmissions(lastWeekLogs);
            const previousWeekTotal = sumEmissions(previousWeekLogs);

            let trendDifference = 0;
            if (previousWeekTotal > 0) {
                trendDifference = parseFloat(((lastWeekTotal - previousWeekTotal) / previousWeekTotal * 100).toFixed(1));
            } else if (lastWeekTotal > 0) {
                trendDifference = 100;
            }

            const totals = { 
                Travel: 0, 
                Diet: 0, 
                Purchasing: 0, 
                Total: 0,
                Trend: trendDifference,
            };

            lastWeekLogs.forEach(log => {
                totals[log.category] = (totals[log.category] || 0) + log.co2e;
                totals.Total += log.co2e;
            });
            
            Object.keys(totals).forEach(key => {
                if (key !== 'Trend') {
                    totals[key] = parseFloat(totals[key].toFixed(2));
                }
            });
            
            setCurrentEmissions(totals);
            
            // --- Gamification Logic Update ---
            const totalWeeklyEmissions = totals.Total;
            let newLevel = 1;
            let newStatus = { emoji: '🌱', text: 'Dormant Seed' };

            if (totalWeeklyEmissions < 80) { 
                newLevel = 2;
                newStatus = { emoji: '🌿', text: 'Sprouting' };
            }
            if (totalWeeklyEmissions < 50) { 
                newLevel = 3;
                newStatus = { emoji: '🌸', text: 'Budding Growth' };
            }
            if (totalWeeklyEmissions < 25) { 
                newLevel = 4;
                newStatus = { emoji: '🌳', text: 'Full Bloom!' };
            }

            setBloomLevel(newLevel);
            setBloomStatus(newStatus);
            // ---------------------------------

            setIsLoading(false);
        }, (error) => {
            console.error("Firestore onSnapshot failed:", error);
            setStatusMessage(`Error fetching data: ${error.message}`);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [db, userId, isAuthReady]);

    // --- 3. Real-Time Data Fetching (Public Leaderboard) Effect ---
    useEffect(() => {
        if (!isAuthReady || !userId || !db) {
            return;
        }

        const leaderboardRef = collection(db, getLeaderboardCollectionPath());
        const q = query(leaderboardRef); 

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const leaders = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                userId: doc.data().userId, 
                displayId: doc.data().userId.substring(0, 8), 
                totalWeeklyCO2e: parseFloat(doc.data().totalWeeklyCO2e),
            }));
            
            leaders.sort((a, b) => a.totalWeeklyCO2e - b.totalWeeklyCO2e); 
            
            setLeaderboardData(leaders);
        }, (error) => {
            console.error("Firestore Leaderboard onSnapshot failed:", error);
        });

        return () => unsubscribe();
    }, [db, userId, isAuthReady]); 

    // --- Input Handlers ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;
        if (name === 'quantity') {
            newValue = Math.max(1, parseInt(value) || 1); 
        }
        
        if (name === 'category') {
            let defaultType;
            const availableTypes = Object.keys(EMISSION_FACTORS_SOURCE).filter(key => key.startsWith(newValue.split('')[0] || '')).slice(0, 1);
            defaultType = availableTypes.length > 0 ? availableTypes[0] : Object.keys(EMISSION_FACTORS_SOURCE)[0];
            
            setInputData(prev => ({ ...prev, [name]: newValue, type: defaultType }));
        } else {
            setInputData(prev => ({ ...prev, [name]: newValue }));
        }
    };


    // --- Data Submission & Leaderboard Update ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!db || !userId) {
            setStatusMessage("Application not ready. Please wait for initialization.");
            return;
        }

        setIsSubmitting(true);
        setStatusMessage('Establishing network connection to Emission Factor API...');
        
        try {
            const co2e = await calculateCarbonAsync(inputData.type, inputData.quantity);
            setStatusMessage(`Successfully fetched factor. Logging ${co2e} kg CO2e...`);
            
            // 1. Log Private Activity
            const logsRef = collection(db, getUserDataCollectionPath(userId));
            await setDoc(doc(logsRef), {
                category: inputData.category,
                type: inputData.type,
                quantity: inputData.quantity,
                co2e: co2e,
                timestamp: new Date().toISOString(),
            });

            const estimatedNewWeeklyTotal = currentEmissions.Total + co2e;

            // 2. Update Public Leaderboard Score
            const leaderboardDocRef = doc(db, getLeaderboardCollectionPath(), userId);
            await setDoc(leaderboardDocRef, {
                userId: userId,
                totalWeeklyCO2e: parseFloat(estimatedNewWeeklyTotal.toFixed(2)), 
                bloomLevel: bloomLevel,
                lastUpdate: new Date().toISOString(),
            }, { merge: true });

            setStatusMessage(`Transaction complete: Logged ${co2e} kg CO2e. Your Bloom is responding!`);
            setInputData(prev => ({ ...prev, quantity: 1 }));
            setTimeout(() => setStatusMessage(''), 5000);
            
            setShowOnboarding(false);
            
        } catch (error) {
            console.error("Error logging carbon activity or updating leaderboard:", error);
            setStatusMessage(`CRITICAL ERROR: Could not complete transaction. ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // --- Data Deletion Utility ---
    const handleDeleteLog = async (logId) => {
        if (!db || !userId) {
            setStatusMessage("Application not ready. Cannot delete log.");
            return;
        }
        
        try {
            const docRef = doc(db, getUserDataCollectionPath(userId), logId);
            await deleteDoc(docRef);
            setStatusMessage('Log deleted successfully.');
        } catch (error) {
            console.error("Error deleting log:", error);
            setStatusMessage(`Error: Could not delete log. ${error.message}`);
        } finally {
            setTimeout(() => setStatusMessage(''), 3000);
        }
    };
    // ----------------------------

    const topCategory = Object.entries(currentEmissions)
        .filter(([key]) => key !== 'Total' && key !== 'Trend')
        .sort(([, a], [, b]) => b - a)[0]?.[0];

    const recommendedActions = topCategory 
        ? bloomActions.filter(a => a.category === topCategory || a.category === 'General') 
        : bloomActions.filter(a => a.category === 'General');
        
    // --- UI Components ---
    const CategoryIcon = ({ category }) => {
        switch (category) {
            case 'Travel': return <Plane className="w-5 h-5 text-indigo-500" />;
            case 'Diet': return <Utensils className="w-5 h-5 text-green-700" />;
            case 'Purchasing': return <RefreshCcw className="w-5 h-5 text-yellow-500" />;
            default: return null;
        }
    };
    
    const CarbonInputForm = () => (
        <form onSubmit={handleSubmit} className="card input-form">
            {/* Changed from h3 to h2 for clearer visual hierarchy within the card */}
            <h2>
                <Zap className="w-6 h-6 mr-2 text-indigo-600"/> Energy Input
            </h2>
            
            {/* Category Select */}
            <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700">Emission Category</label>
                <select
                    id="category"
                    name="category"
                    value={inputData.category}
                    onChange={handleInputChange}
                    className="input-field" 
                >
                    <option value="Travel">Travel (e.g., driving, flights)</option>
                    <option value="Diet">Diet (e.g., meal choices)</option>
                    <option value="Purchasing">Purchasing (e.g., goods, apparel)</option>
                </select>
            </div>

            {/* Type Select */}
            <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700">Activity Type</label>
                <select
                    id="type"
                    name="type"
                    value={inputData.type}
                    onChange={handleInputChange}
                    className="input-field"
                >
                    {Object.keys(EMISSION_FACTORS_SOURCE).filter(key => key.startsWith(inputData.category.split('')[0] || '')).map(key => (
                        <option key={key} value={key}>{key.replace(/_/g, ' ')}</option>
                    ))}
                </select>
            </div>

            {/* Quantity Input */}
            <div>
                <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                    Quantity (Units: {inputData.category === 'Travel' ? 'x100 km' : 'times'})
                </label>
                <input
                    type="number"
                    id="quantity"
                    name="quantity"
                    min="1"
                    value={inputData.quantity}
                    onChange={handleInputChange}
                    className="input-field"
                />
            </div>

            <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className="btn-submit" 
            >
                {isSubmitting ? 'Contacting API...' : `Log Activity (${liveCarbonCost.toFixed(2)} kg CO2e)`}
            </button>
            
            {statusMessage && (
                <p className={`text-sm text-center font-medium mt-2 ${statusMessage.startsWith('Error') || statusMessage.includes('CRITICAL') ? 'text-red-600' : 'text-green-600'}`}>
                    {statusMessage}
                </p>
            )}
            
            <p className="text-xs text-gray-500 mt-4 text-center">Your Full User ID (for collaboration): <span className="font-mono text-gray-700 break-all">{userId || 'Authenticating...'}</span></p>

        </form>
    );
    
    const BloomProgressCard = ({ level, status }) => (
        <div className="card card-border-green text-center">
            {/* Changed from h3 to h2 for clearer visual hierarchy within the card */}
            <h2>
                <Award className="w-6 h-6 mr-2 text-yellow-500"/> Your EcoBloom Status
            </h2>
            <div className="bloom-status-emoji">
                {status.emoji}
            </div>
            <p className="level-text">
                Level {level}
            </p>
            <p className="text-lg font-medium text-gray-600">
                {status.text}
            </p>
            <p className="text-xs text-gray-500 mt-3">
                Current goal: Reach Level 4 by keeping weekly emissions below 25 kg CO2e!
            </p>
        </div>
    );

    const BloomActionsPanel = ({ actions }) => (
        <div className="card">
            {/* Changed from h3 to h2 for clearer visual hierarchy within the card */}
            <h2>
                <Leaf className="w-6 h-6 mr-2 text-green-700" /> Local Bloom Strategies
            </h2>
            <p className="text-sm text-gray-600 mb-4">
                Your highest impact area is currently **{topCategory || 'General'}**. Focus here for maximum growth!
            </p>
            <div className="space-y-3">
                {actions.slice(0, 3).map((action, index) => (
                    <div key={index} className="flex items-start p-3 bg-green-50 rounded-lg border-l-4 border-green-400 hover:shadow-md transition duration-150">
                        <action.icon className="w-5 h-5 mt-1 text-green-700 flex-shrink-0" />
                        <div className="ml-3">
                            <p className="text-sm font-bold text-green-800">{action.title}</p>
                            <p className="text-xs text-green-700">{action.description}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const TrendSummaryCard = ({ trend }) => {
        const isNegative = trend < 0; 
        const isNeutral = trend === 0;

        const TrendIconComponent = isNeutral ? Minus : (isNegative ? TrendingUp : TrendingDown);
        
        const trendColor = isNeutral ? 'text-gray-500' : (isNegative ? 'text-red-500' : 'text-green-700');
        const trendBgColor = isNeutral ? 'bg-gray-100' : (isNegative ? 'bg-red-50' : 'bg-green-50');
        const trendText = isNeutral ? 'No Change' : (isNegative ? 'Increase' : 'Reduction');
        const trendValue = Math.abs(trend).toFixed(1);

        return (
            // REMOVED 'flex flex-col' to ensure stat-card relies on CSS display property for layout
            <div className={`stat-card ${trendBgColor} transform hover:scale-105 transition duration-150 border-2 ${isNegative ? 'border-red-200' : 'border-green-200'}`}>
                <div className="text-sm font-medium text-gray-600 mb-1">vs. Last Period</div>
                <div className={`text-2xl font-bold flex items-center justify-center ${trendColor}`}> {/* ADDED justify-center */}
                    <TrendIconComponent className="w-6 h-6 mr-2" />
                    {trendValue}{trend !== 0 && '%'}
                </div>
                <div className={`text-xs font-semibold ${trendColor}`}>{trendText} in Total Emissions</div>
            </div>
        );
    };

    const EmissionSummaryCard = ({ title, value, category, color }) => (
        // REMOVED 'flex flex-col' to ensure stat-card relies on CSS display property for layout
        <div className={`stat-card ${color} bg-opacity-10 transform hover:scale-105 transition duration-150`}>
            <div className="text-sm font-medium text-gray-600 mb-1">{title}</div>
            <div className={`text-2xl font-bold ${color.replace('bg-', 'text-').replace('100', '700')}`}>
                {value}
            </div>
            {category && <CategoryIcon category={category} />}
        </div>
    );
    
    const RecentActivityPanel = ({ logs }) => (
        <div className="card border border-gray-100">
            {/* Changed from h2 to h3, as this is a secondary detail panel */}
            <h3>Recent Activity (Last 5)</h3>
            <ul className="space-y-3 max-h-48 overflow-y-auto pr-2">
                {logs.slice(-5).reverse().map((log) => (
                    <li key={log.id} className="leaderboard-item flex justify-between items-center p-3 bg-gray-50 rounded-lg shadow-sm border-l-4 border-indigo-400">
                        <div className="flex flex-col flex-grow">
                            <span className="text-sm font-semibold text-gray-800">
                                {log.co2e} kg CO2e logged
                            </span>
                            <span className="text-xs text-gray-600 capitalize">
                                {log.type.replace(/_/g, ' ')} ({log.quantity} units)
                            </span>
                            <span className="text-xs text-gray-400">
                                {formatChartDate(log.timestamp)} at {formatLogTime(log.timestamp)}
                            </span>
                        </div>
                        <button
                            onClick={() => handleDeleteLog(log.id)}
                            className="text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition duration-150"
                            title="Delete Log"
                        >
                            <Trash2 className="w-5 h-5" />
                        </button>
                    </li>
                ))}
            </ul>
            {logs.length === 0 && <p className="text-center text-gray-500 text-sm">No activity logged yet. Start logging above!</p>}
        </div>
    );

    const LeaderboardPanel = ({ leaders, currentUserId }) => (
        <div className="card border border-gray-100">
            {/* Changed from h2 to h3, as this is a secondary detail panel */}
            <h3>
                <Globe className="w-5 h-5 mr-2 text-indigo-500"/> Global Bloom Leaderboard
            </h3>
            <p className="text-sm text-gray-600 mb-4">Top 5 users with the lowest weekly CO2e (kg).</p>
            <ul className="space-y-2">
                {leaders.slice(0, 5).map((leader, index) => {
                    const isCurrentUser = leader.userId === currentUserId;
                    return (
                        <li 
                            key={leader.id} 
                            className={`leaderboard-item flex justify-between items-center p-3 rounded-lg border-l-4 transition duration-200 ${
                                isCurrentUser 
                                ? 'bg-yellow-50 border-yellow-400 font-bold shadow-md scale-[1.01]' 
                                : 'bg-gray-50 border-indigo-100'
                            }`}
                        >
                            <div className="flex items-center">
                                <span className={`w-6 text-center text-lg font-extrabold ${isCurrentUser ? 'text-yellow-600' : 'text-gray-500'}`}>{index + 1}</span>
                                <span className="ml-3 font-mono text-sm">
                                    {isCurrentUser ? 'You (Current User)' : `User ${leader.displayId}...`}
                                </span>
                            </div>
                            <div className={`text-right text-lg font-bold ${leader.totalWeeklyCO2e < 50 ? 'text-green-700' : 'text-red-500'}`}>
                                {leader.totalWeeklyCO2e.toFixed(1)} kg
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );


    const chartData = carbonLogs.reduce((acc, log) => {
        const dateKey = formatChartDate(log.timestamp); 
        
        let dayEntry = acc.find(d => d.date === dateKey);
        
        if (!dayEntry) {
            dayEntry = { 
                date: dateKey, 
                Total: 0, 
                Travel: 0, 
                Diet: 0, 
                Purchasing: 0 
            };
            acc.push(dayEntry);
        }
        
        dayEntry.Total = parseFloat((dayEntry.Total + log.co2e).toFixed(2));
        dayEntry[log.category] = parseFloat((dayEntry[log.category] + log.co2e).toFixed(2));

        return acc;
    }, []);

    const displayChartData = chartData.slice(-7);


    if (isLoading || !isAuthReady) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-500"></div>
                <p className="ml-4 text-lg text-gray-700">Establishing Secure Connection (Nourishing the system...)</p>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-gradient-to-br from-white via-green-50 to-indigo-50 font-sans p-4 sm:p-8">
            <header className="text-center mb-10">
                <h1>
                    <TreeDeciduous className="w-12 h-12 mr-3 text-green-600 animate-pulse" />
                    EcoBloom Tracker
                </h1>
                <p className="text-xl text-gray-600 mt-2 font-light">Your journey from winter emissions to spring renewal.</p>
            </header>

            <main className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                
                {/* COLUMN 1: Input and Gamification */}
                <div className="lg:col-span-1 space-y-6">
                    <CarbonInputForm />
                    <BloomProgressCard level={bloomLevel} status={bloomStatus} />
                    <BloomActionsPanel actions={recommendedActions} />
                </div>

                {/* COLUMN 2 & 3: Visualization and Details */}
                <div className="lg:col-span-2 space-y-6">
                    
                    {/* Growth Curve Visualization */}
                    <div className="card border border-gray-100">
                        <h2 className="chart-container-title flex items-center">
                            <Gauge className="w-6 h-6 mr-2 text-indigo-500"/> Emissions Growth Curve (Last 7 Days)
                        </h2>
                        <div className="h-64 sm:h-80 w-full mt-4">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={displayChartData}
                                    margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                    <XAxis dataKey="date" stroke="#6b7280" />
                                    <YAxis 
                                        label={{ value: 'CO2e (kg)', angle: -90, position: 'insideLeft', fill: '#6b7280' }} 
                                        stroke="#6b7280" 
                                        domain={['auto', 'auto']}
                                    />
                                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                    <Legend wrapperStyle={{ paddingTop: '10px' }} />
                                    
                                    <Line 
                                        type="monotone" 
                                        dataKey="Total" 
                                        stroke="#10B981" 
                                        strokeWidth={4} 
                                        dot={{ r: 6, fill: '#10B981', stroke: '#fff', strokeWidth: 2 }} 
                                        activeDot={{ r: 9 }} 
                                    />
                                    
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <p className="text-center text-sm text-gray-500 mt-4">Keep your Total line trending downwards for optimal **Bloom** conditions!</p>
                    </div>

                    {/* Weekly Snapshot & Trend Comparison */}
                    <div className="card border border-gray-100">
                        <h2>Weekly Emission Snapshot (Last 7 Logs)</h2>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <TrendSummaryCard trend={currentEmissions.Trend} />
                            <EmissionSummaryCard 
                                title="Total Emissions" 
                                value={`${currentEmissions.Total} kg`} 
                                color="bg-gray-100" 
                            />
                            <EmissionSummaryCard 
                                title="Travel" 
                                value={`${currentEmissions.Travel} kg`} 
                                category="Travel"
                                color="bg-indigo-500"
                            />
                            <EmissionSummaryCard 
                                title="Diet" 
                                value={`${currentEmissions.Diet} kg`} 
                                category="Diet"
                                color="bg-lime-500"
                            />
                            <EmissionSummaryCard 
                                title="Purchasing" 
                                value={`${currentEmissions.Purchasing} kg`} 
                                category="Purchasing"
                                color="bg-yellow-500"
                            />
                        </div>
                    </div>
                    
                    {/* Public Leaderboard and Recent Activity are now side-by-side */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <LeaderboardPanel leaders={leaderboardData} currentUserId={userId} />
                        <RecentActivityPanel logs={carbonLogs} />
                    </div>
                </div>
            </main>
            
            {/* Onboarding Modal */}
            {showOnboarding && <OnboardingModal onClose={() => setShowOnboarding(false)} />}
        </div>
    );
};

export default App;